import React, { useEffect, useState } from 'react';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Alert, Autocomplete, Box, Checkbox, TextField } from '@mui/material';
import { orgApi } from '../../api/client';
import { actionsStyle, cancelButton, errorBanner, formStyle, generatePassword, inputStyle, labelStyle, pageStyle, primaryButton } from '../formUi';

type Section = 'business-units' | 'stores' | 'managers' | 'organization';

const cardStyle: React.CSSProperties = {
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  padding: '1.5rem',
  backgroundColor: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

const primaryBtn: React.CSSProperties = {
  padding: '0.5rem 1rem',
  backgroundColor: '#1976d2',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
};

const ghostBtn: React.CSSProperties = {
  padding: '0.5rem 1rem',
  backgroundColor: '#f5f5f5',
  border: '1px solid #ccc',
  borderRadius: '4px',
  cursor: 'pointer',
};

const iconBtn = (backgroundColor: string): React.CSSProperties => ({
  width: 32,
  height: 32,
  padding: 0,
  backgroundColor,
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  flexShrink: 0,
  lineHeight: 1,
});

const OrgAdminPage: React.FC<{ section?: Section }> = ({ section = 'business-units' }) => {
  const [bus, setBus] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [managers, setManagers] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<'bu' | 'store' | 'manager' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<any>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = async () => {
    try {
      setError(null);
      const [b, s, m, e] = await Promise.all([
        orgApi.listBusinessUnits(),
        orgApi.listStores(),
        orgApi.listManagers(),
        orgApi.listEmployees(),
      ]);
      setBus(b);
      setStores(s);
      setManagers(m);
      setEmployees(e);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      if (open === 'bu') {
        if (form.id) await orgApi.updateBusinessUnit(form.id, form);
        else await orgApi.createBusinessUnit(form);
      }
      if (open === 'store') {
        if (form.id) await orgApi.updateStore(form.id, form);
        else await orgApi.createStore(form);
      }
      if (open === 'manager') {
        const storeIds = form.storeIds?.length ? form.storeIds : form.storeId ? [form.storeId] : [];
        if (!storeIds.length) {
          setError('Select at least one store.');
          return;
        }
        const payload = {
          ...form,
          storeIds,
          storeId: storeIds[0],
          temporaryPassword: form.temporaryPassword,
        };
        if (form.id) await orgApi.updateManager(form.id, payload);
        else await orgApi.createManager(payload);
      }
      setOpen(null);
      setForm({});
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const header = (title: string, onCreate?: () => void, createLabel?: string) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
      <h3 style={{ margin: 0 }}>{title}</h3>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {onCreate && (
          <button type="button" style={primaryBtn} onClick={onCreate}>
            {createLabel}
          </button>
        )}
        <button type="button" style={ghostBtn} onClick={load}>
          🔄 Refresh
        </button>
      </div>
    </div>
  );

  if (open) {
    const title = open === 'bu' ? (form.id ? 'Edit Business Unit' : 'Create New Business Unit') : open === 'store' ? (form.id ? 'Edit Store' : 'Create New Store') : (form.id ? 'Edit Manager' : 'Create New Manager');
    const submitLabel = open === 'bu' ? (form.id ? 'Save Business Unit' : 'Create Business Unit') : open === 'store' ? (form.id ? 'Save Store' : 'Create Store') : (form.id ? 'Save Manager' : 'Create Manager');
    return (
      <div style={pageStyle(600)}>
        <h2 style={open === 'manager' ? { textAlign: 'center' } : undefined}>{title}</h2>
        <form onSubmit={submit} style={formStyle}>
          {error && <div style={errorBanner}>{error}</div>}
          {open === 'store' && (
            <div>
              <label style={labelStyle}>Business Unit *</label>
              <select required value={form.businessUnitId || ''} onChange={(e) => setForm({ ...form, businessUnitId: e.target.value })} style={{ ...inputStyle, background: 'white' }}>
                <option value="">Select a Business Unit</option>
                {bus.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          )}
          {open === 'manager' && (
            <div>
              <label style={labelStyle}>Stores <span style={{ color: '#d32f2f' }}>*</span></label>
              <Autocomplete
                multiple
                fullWidth
                disableCloseOnSelect
                options={stores}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={stores.filter((s) => (form.storeIds || []).includes(s.id))}
                onChange={(_, selected) => setForm({ ...form, storeIds: selected.map((s) => s.id) })}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      checked={selected}
                      sx={{ mr: 1, p: 0.5 }}
                    />
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} placeholder={(form.storeIds || []).length ? '' : 'Select one or more stores'} />
                )}
              />
            </div>
          )}
          <div>
            <label style={labelStyle}>{open === 'bu' ? 'Business Unit Name *' : open === 'store' ? 'Store Name *' : <>Full Name <span style={{ color: '#d32f2f' }}>*</span></>}</label>
            <input
              required
              value={form.name || ''}
              placeholder={open === 'bu' ? 'North Region, West Division, etc.' : open === 'store' ? 'Store #123, Downtown Location, etc.' : 'John Doe'}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={inputStyle}
            />
          </div>
          {open === 'manager' && (
            <div>
              <label style={labelStyle}>Phone Number</label>
              <input type="tel" value={form.phoneNumber || ''} placeholder="+1 (416) 555-1234" onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} style={inputStyle} />
              <small style={{ color: '#666', fontSize: '0.8rem' }}>Optional - Canadian format: +1 (XXX) XXX-XXXX or (XXX) XXX-XXXX</small>
            </div>
          )}
          {open === 'manager' && (
            <div>
              <label style={labelStyle}>Email Address <span style={{ color: '#d32f2f' }}>*</span></label>
              <input required type="email" value={form.email || ''} placeholder="manager@company.com" onChange={(e) => setForm({ ...form, email: e.target.value })} style={inputStyle} />
            </div>
          )}
          {(open === 'bu' || open === 'store') && (
            <div>
              <label style={labelStyle}>Description</label>
              <textarea
                rows={4}
                value={form.description || ''}
                placeholder={open === 'bu' ? 'Optional description of the business unit' : 'Optional description of the store'}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                style={{ ...inputStyle, fontFamily: 'inherit' }}
              />
            </div>
          )}
          {open === 'manager' && !form.id && (
            <div>
              <label style={labelStyle}>Temporary Password <span style={{ color: '#d32f2f' }}>*</span></label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input required value={form.temporaryPassword || ''} placeholder="Enter temporary password" onChange={(e) => setForm({ ...form, temporaryPassword: e.target.value })} style={{ ...inputStyle, flex: 1 }} />
                <button type="button" onClick={() => setForm({ ...form, temporaryPassword: generatePassword() })} style={{ padding: '0.75rem 1rem', background: '#f5f5f5', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>
                  Generate
                </button>
              </div>
              <small style={{ color: '#666', fontSize: '0.8rem' }}>Password must contain uppercase, lowercase, number, and special character (min 8 chars)</small>
            </div>
          )}
          <div style={actionsStyle}>
            <button type="button" onClick={() => { setOpen(null); setError(null); }} style={cancelButton}>Cancel</button>
            <button type="submit" disabled={submitting} style={primaryButton(submitting)}>{submitting ? (form.id ? 'Saving...' : 'Creating...') : submitLabel}</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {section === 'business-units' && (
        <div>
          {header(`Business Units (${bus.length})`, () => { setForm({}); setOpen('bu'); }, '+ Create Business Unit')}
          {bus.length === 0 && <p>No business units found. Create your first business unit to get started.</p>}
          <div style={{ display: 'grid', gap: '1rem' }}>
            {bus.map((b) => (
              <div key={b.id} style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem' }}>
                  <div style={{ minWidth: 0 }}>
                    <h4 style={{ margin: 0, color: '#1976d2', marginBottom: '0.5rem' }}>{b.name}</h4>
                    {b.description && <p style={{ margin: '0.5rem 0', color: '#666' }}>{b.description}</p>}
                    <p style={{ margin: '0.5rem 0', color: '#999', fontSize: '0.9rem' }}>
                      Created: {b.createdAt ? new Date(b.createdAt).toLocaleDateString() : '—'}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                    <button
                      type="button"
                      title="Edit"
                      aria-label={`Edit ${b.name}`}
                      style={iconBtn('#1976d2')}
                      onClick={() => {
                        setError(null);
                        setForm({
                          id: b.id,
                          name: b.name,
                          description: b.description || '',
                        });
                        setOpen('bu');
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      title="Delete"
                      aria-label={`Delete ${b.name}`}
                      style={iconBtn('#d32f2f')}
                      onClick={async () => {
                        if (!window.confirm(`Delete business unit "${b.name}"?`)) return;
                        try {
                          setError(null);
                          await orgApi.deleteBusinessUnit(b.id);
                          await load();
                        } catch (err: any) {
                          setError(err?.response?.data?.error || err.message);
                        }
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {section === 'stores' && (
        <div>
          {header(`Stores (${stores.length})`, () => { setForm({ businessUnitId: bus[0]?.id || '' }); setOpen('store'); }, '+ Create Store')}
          {stores.length === 0 && <p>No stores found.</p>}
          <div style={{ display: 'grid', gap: '1rem' }}>
            {stores.map((s) => (
              <div key={s.id} style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem' }}>
                  <div style={{ minWidth: 0 }}>
                    <h4 style={{ margin: 0, color: '#1976d2' }}>{s.name}</h4>
                    <p style={{ margin: '0.5rem 0', color: '#666' }}>{s.businessUnit?.name || 'No business unit'}</p>
                    {s.description && <p style={{ margin: 0, color: '#666' }}>{s.description}</p>}
                  </div>
                  <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                    <button
                      type="button"
                      title="Edit"
                      aria-label={`Edit ${s.name}`}
                      style={iconBtn('#1976d2')}
                      onClick={() => {
                        setError(null);
                        setForm({
                          id: s.id,
                          name: s.name,
                          description: s.description || '',
                          businessUnitId: s.businessUnitId || s.businessUnit?.id || '',
                        });
                        setOpen('store');
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      title="Delete"
                      aria-label={`Delete ${s.name}`}
                      style={iconBtn('#d32f2f')}
                      onClick={async () => {
                        if (!window.confirm(`Delete store "${s.name}"?`)) return;
                        try {
                          setError(null);
                          await orgApi.deleteStore(s.id);
                          await load();
                        } catch (err: any) {
                          setError(err?.response?.data?.error || err.message);
                        }
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {section === 'managers' && (
        <div>
          {header(`Managers (${managers.length})`, () => { setForm({ storeIds: [] }); setOpen('manager'); }, '+ Create Manager')}
          {managers.length === 0 && <p>No managers found.</p>}
          {managers.length > 0 && (
            <div style={{ overflowX: 'auto', background: 'white', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#1976d2', color: 'white', textAlign: 'left' }}>
                    {['Name', 'Email', 'Phone No', 'Store', 'Action'].map((col) => (
                      <th key={col} style={{ padding: '0.85rem 1rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {managers.map((m) => {
                    const linked = (m.managerStores || [])
                      .map((ms: any) => ms.store?.name || stores.find((s) => s.id === ms.storeId)?.name)
                      .filter(Boolean);
                    const storeNames = linked.length
                      ? linked.join(', ')
                      : m.store?.name || stores.find((s) => s.id === m.storeId)?.name || '—';
                    const storeIds = (m.managerStores || []).map((ms: any) => ms.storeId).filter(Boolean);
                    return (
                      <tr key={m.id} style={{ borderTop: '1px solid #e0e0e0' }}>
                        <td style={{ padding: '0.85rem 1rem' }}>{m.name}</td>
                        <td style={{ padding: '0.85rem 1rem' }}>{m.email}</td>
                        <td style={{ padding: '0.85rem 1rem' }}>{m.phoneNumber || '—'}</td>
                        <td style={{ padding: '0.85rem 1rem' }}>{storeNames}</td>
                        <td style={{ padding: '0.85rem 1rem' }}>
                          <div style={{ display: 'flex', gap: '0.35rem' }}>
                            <button
                              type="button"
                              title="Edit"
                              aria-label={`Edit ${m.name}`}
                              style={iconBtn('#1976d2')}
                              onClick={() => {
                                setError(null);
                                setForm({
                                  id: m.id,
                                  name: m.name,
                                  email: m.email,
                                  phoneNumber: m.phoneNumber || '',
                                  storeIds: storeIds.length ? storeIds : m.storeId ? [m.storeId] : [],
                                });
                                setOpen('manager');
                              }}
                            >
                              ✏️
                            </button>
                            <button
                              type="button"
                              title="Delete"
                              aria-label={`Delete ${m.name}`}
                              style={iconBtn('#d32f2f')}
                              onClick={async () => {
                                if (!window.confirm(`Delete manager "${m.name}"?`)) return;
                                try {
                                  setError(null);
                                  await orgApi.deleteManager(m.id);
                                  await load();
                                } catch (err: any) {
                                  setError(err?.response?.data?.error || err.message);
                                }
                              }}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {section === 'organization' && (
        <div>
          {header('Organization Hierarchy')}
          {bus.length === 0 && <p>No business units found. Create your first business unit to get started.</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {bus.map((bu) => {
              const buStores = stores.filter((s) => s.businessUnitId === bu.id || s.businessUnit?.id === bu.id);
              const openBu = expanded.has(`bu-${bu.id}`);
              return (
                <div key={bu.id} style={{ border: '2px solid #1976d2', borderRadius: 8, background: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                  <div onClick={() => toggle(`bu-${bu.id}`)} style={{ padding: '1.5rem', cursor: 'pointer', background: '#e3f2fd' }}>
                    <h3 style={{ margin: 0, color: '#1976d2' }}>
                      {openBu ? '▼' : '▶'} 🏢 {bu.name}
                    </h3>
                    {bu.description && <p style={{ margin: '0.5rem 0 0', color: '#666' }}>{bu.description}</p>}
                    <p style={{ margin: '0.5rem 0 0', color: '#999', fontSize: '0.85rem' }}>{buStores.length} Store{buStores.length === 1 ? '' : 's'}</p>
                  </div>
                  {openBu && (
                    <div style={{ padding: '1rem', background: '#fafafa' }}>
                      {buStores.length === 0 && <p style={{ color: '#999', fontStyle: 'italic' }}>No stores in this business unit</p>}
                      {buStores.map((store) => {
                        const storeManagers = managers.filter((m) => m.storeId === store.id || m.store?.id === store.id);
                        const openStore = expanded.has(`store-${store.id}`);
                        return (
                          <div key={store.id} style={{ marginBottom: '1rem', border: '1px solid #4caf50', borderRadius: 6, background: 'white', overflow: 'hidden' }}>
                            <div onClick={() => toggle(`store-${store.id}`)} style={{ padding: '1rem', cursor: 'pointer', background: '#e8f5e9' }}>
                              <h4 style={{ margin: 0, color: '#2e7d32', marginLeft: '1.5rem' }}>
                                {openStore ? '▼' : '▶'} 🏪 {store.name}
                              </h4>
                              <p style={{ margin: '0.5rem 0 0 1.5rem', color: '#999', fontSize: '0.8rem' }}>
                                {storeManagers.length} Manager{storeManagers.length === 1 ? '' : 's'}
                              </p>
                            </div>
                            {openStore && (
                              <div style={{ padding: '0.75rem', background: '#f9f9f9' }}>
                                {storeManagers.length === 0 && <p style={{ color: '#999', fontStyle: 'italic', marginLeft: '1.5rem' }}>No managers in this store</p>}
                                {storeManagers.map((manager) => {
                                  const emps = employees.filter((e) => e.managerId === manager.id);
                                  const openMgr = expanded.has(`manager-${manager.id}`);
                                  return (
                                    <div key={manager.id} style={{ marginBottom: '0.75rem', border: '1px solid #ff9800', borderRadius: 4, background: 'white' }}>
                                      <div onClick={() => toggle(`manager-${manager.id}`)} style={{ padding: '0.75rem 1rem', cursor: 'pointer', background: '#fff3e0' }}>
                                        <strong style={{ color: '#e65100' }}>
                                          {openMgr ? '▼' : '▶'} 👥 {manager.name}
                                        </strong>
                                        <div style={{ color: '#666', fontSize: '0.85rem' }}>{manager.email} · {emps.length} employee{emps.length === 1 ? '' : 's'}</div>
                                      </div>
                                      {openMgr && (
                                        <div style={{ padding: '0.75rem 1.5rem' }}>
                                          {emps.length === 0 && <span style={{ color: '#999' }}>No employees</span>}
                                          {emps.map((e) => (
                                            <div key={e.id} style={{ padding: '0.35rem 0', color: '#333' }}>
                                              👤 {e.name} — {e.email}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

    </Box>
  );
};

export default OrgAdminPage;
